/**
 * Market Hub Read-Only API Client (Task 001b).
 * 
 * Invariants:
 * - Strictly Read-Only: All mutating HTTP operations (POST, PUT, PATCH, DELETE) fail closed.
 * - Zero-Float Boundary: Numeric financial fields decoded via parseDecimalString().
 * - 12 Frozen Routes Coverage: Direct typed bindings to all canonical endpoints.
 * - Deterministic error boundaries with zero credential leakage.
 */

import { parseDecimalString, DecimalString } from '../shared/domain/decimal';

export class MutationOperationForbiddenError extends Error {
  constructor(method: string) {
    super(`MUTATION_OPERATION_FORBIDDEN: HTTP method '${method}' is strictly prohibited on Market Hub client.`);
    this.name = 'MutationOperationForbiddenError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'HEAD' | 'OPTIONS' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  params?: Record<string, string | number>;
  signal?: AbortSignal;
}

export class ReadOnlyApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
    if (typeof window !== 'undefined') {
      this.token = (window as any).__MARKET_HUB_TOKEN__ || localStorage.getItem('market_hub_auth_token') || null;
    }
  }

  public setToken(token: string | null): void {
    this.token = token;
    if (typeof window !== 'undefined' && token) {
      localStorage.setItem('market_hub_auth_token', token);
    }
  }

  public getToken(): string | null {
    return this.token;
  }

  public async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const method = options.method || 'GET';

    // Invariant Enforcement: Reject any mutating HTTP method immediately
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
      throw new MutationOperationForbiddenError(method);
    }

    let url = `${this.baseUrl}${endpoint}`;
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
          searchParams.append(key, String(val));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    const authHeaders: Record<string, string> = {};
    if (this.token) {
      authHeaders['Authorization'] = `Bearer ${this.token}`;
      authHeaders['X-TSO-Timestamp-UTC'] = new Date().toISOString();
      authHeaders['X-TSO-Nonce'] = `nonce-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
      authHeaders['X-TSO-Tenant-ID'] = 'TENANT_TSO_CRYPTO';
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Accept': 'application/json',
          ...authHeaders,
          ...(options.headers || {}),
        },
        signal: options.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP_${response.status}: ${response.statusText}`);
      }

      return (await response.json()) as T;
    } catch (err: unknown) {
      throw err;
    }
  }

  public async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  // --- 12 Frozen Endpoints Typed Methods ---

  /** 1. Health Probe */
  public async getHealth(): Promise<{ status: string }> {
    return this.get<{ status: string }>('/health');
  }

  /** 2. Market Hub Status */
  public async getMarketStatus(): Promise<{ status: string; p95_latency_ms: string; cached_instruments: number }> {
    return this.get('/api/v1/market/status');
  }

  /** 3. Candlesticks */
  public async getCandles(symbol: string = 'BTC-USDT', timeframe: string = '1h', limit: number = 100): Promise<{
    symbol: string;
    timeframe: string;
    candles: Array<{
      timestamp: number;
      open: DecimalString;
      high: DecimalString;
      low: DecimalString;
      close: DecimalString;
      volume: DecimalString;
    }>;
  }> {
    const res = await this.get<{
      symbol: string;
      timeframe: string;
      candles: Array<{
        timestamp: number;
        open: string;
        high: string;
        low: string;
        close: string;
        volume: string;
      }>;
    }>('/api/v1/market/candles', { params: { symbol, timeframe, limit } });

    // Enforce Decimal invariant on parsed candles
    return {
      ...res,
      candles: (res.candles || []).map(c => ({
        timestamp: c.timestamp,
        open: parseDecimalString(c.open),
        high: parseDecimalString(c.high),
        low: parseDecimalString(c.low),
        close: parseDecimalString(c.close),
        volume: parseDecimalString(c.volume),
      })),
    };
  }

  /** 3b. Canonical Candlesticks (Stage 091 - Zero-Backend Bounded Candles Contract) */
  public async getCanonicalCandles(
    symbol: string = 'BTCUSDT',
    timeframe: string = '1h',
    limit: number = 100,
    market_type: string = 'SPOT'
  ): Promise<CanonicalCandlesResponseV1> {
    const res = await this.get<{
      symbol: string;
      market_type: string;
      timeframe: string;
      count: number;
      format: string;
      candles: Array<{
        candle_id?: string;
        instrument: string | CanonicalInstrumentKeyV1;
        open_time_utc: string;
        close_time_utc: string;
        candle_state?: string;
        open: string;
        high: string;
        low: string;
        close: string;
        volume: string;
      }>;
    }>('/api/v1/market/candles', {
      params: { symbol, market_type, timeframe, format: 'canonical', limit }
    });

    return {
      ...res,
      candles: (res.candles || []).map(c => {
        if (
          typeof c.open !== 'string' ||
          typeof c.high !== 'string' ||
          typeof c.low !== 'string' ||
          typeof c.close !== 'string' ||
          typeof c.volume !== 'string'
        ) {
          throw new Error('CANONICAL_CANDLE_WIRE_ERROR: Wire OHLCV values must be strings');
        }
        return {
          candle_id: c.candle_id,
          instrument: c.instrument,
          open_time_utc: c.open_time_utc,
          close_time_utc: c.close_time_utc,
          candle_state: c.candle_state,
          open: parseDecimalString(c.open),
          high: parseDecimalString(c.high),
          low: parseDecimalString(c.low),
          close: parseDecimalString(c.close),
          volume: parseDecimalString(c.volume),
        };
      }),
    };
  }

  /** 4. Realtime Ticker */
  public async getRealtime(symbol: string = 'BTC-USDT'): Promise<{
    symbol: string;
    price: DecimalString;
    change_24h: DecimalString;
    volume_24h: DecimalString;
  }> {
    const res = await this.get<{
      symbol: string;
      price: string;
      change_24h: string;
      volume_24h: string;
    }>('/api/v1/market/realtime', { params: { symbol } });

    return {
      symbol: res.symbol,
      price: parseDecimalString(res.price),
      change_24h: parseDecimalString(res.change_24h),
      volume_24h: parseDecimalString(res.volume_24h),
    };
  }

  /** 4b. Realtime Prices (Batch/List) */
  public async getRealtimePrices(marketType: string = 'SPOT', options: RequestOptions = {}): Promise<{
    count: number;
    prices: Array<{
      symbol: string;
      market_type: string;
      last_price: string;
      bid_price: string;
      ask_price: string;
      timestamp_utc?: string;
      source_provider?: string;
    }>;
  }> {
    return this.get('/api/v1/market/realtime', { ...options, params: { ...(options.params || {}), market_type: marketType } });
  }

  /** 5b. Basis Core 7 */
  public async getBasisCore7(timeframe: string = '1m', options: RequestOptions = {}): Promise<{
    contract_version?: string;
    scope?: string;
    timeframe?: string;
    as_of_utc?: string;
    count?: number;
    items?: Record<string, {
      symbol: string;
      timeframe: string;
      status: string;
      basis_bps: string | null;
      spot_venue: string | null;
      perp_venue: string | null;
      spot_close: string | null;
      perp_close: string | null;
      description?: string;
    }>;
  }> {
    return this.get('/api/v1/market/basis', { ...options, params: { ...(options.params || {}), scope: 'core7', timeframe } });
  }

  /** 5. Basis & Spread */
  public async getBasis(symbol: string = 'BTC-USDT'): Promise<{
    symbol: string;
    spot_price: DecimalString;
    perp_price: DecimalString;
    basis: DecimalString;
    basis_rate_bps: DecimalString;
    funding_rate: DecimalString;
  }> {
    const res = await this.get<{
      symbol: string;
      spot_price: string;
      perp_price: string;
      basis: string;
      basis_rate_bps: string;
      funding_rate: string;
    }>('/api/v1/market/basis', { params: { symbol } });

    return {
      symbol: res.symbol,
      spot_price: parseDecimalString(res.spot_price),
      perp_price: parseDecimalString(res.perp_price),
      basis: parseDecimalString(res.basis),
      basis_rate_bps: parseDecimalString(res.basis_rate_bps),
      funding_rate: parseDecimalString(res.funding_rate),
    };
  }

  /** 6. Telemetry */
  public async getTelemetry(options: RequestOptions = {}): Promise<Record<string, unknown>> {
    return this.get('/api/v1/market/telemetry', options);
  }

  /** 7. Fact Tags Matrix */
  public async getTagsMatrix(symbol: string = 'BTC-USDT'): Promise<Record<string, unknown>> {
    return this.get('/api/v1/market/tags/matrix', { params: { symbol } });
  }

  /** 8 & 9. Trend Weather Matrix (and canonical alias) */
  public async getTrendWeatherMatrix(bypassCache: boolean = false, options: RequestOptions = {}): Promise<Record<string, unknown>> {
    const params = bypassCache ? { ...(options.params || {}), bypass_cache: 'true' } : options.params;
    return this.get('/api/v2/trend-weather/matrix', { ...options, params });
  }

  public async getMarketTrendWeatherMatrix(bypassCache: boolean = false, options: RequestOptions = {}): Promise<Record<string, unknown>> {
    const params = bypassCache ? { ...(options.params || {}), bypass_cache: 'true' } : options.params;
    return this.get('/api/v2/market/trend-weather-matrix', { ...options, params });
  }

  /** 10. Trend Weather Status */
  public async getTrendWeatherStatus(): Promise<{ status: string; last_resample_utc: string }> {
    return this.get('/api/v2/trend-weather/status');
  }

  /** 11. Indicator Resonance Matrix */
  public async getIndicatorsMatrix(symbol: string = 'BTCUSDT', bypassCache: boolean = false, options: RequestOptions = {}): Promise<Record<string, unknown>> {
    const params: Record<string, string> = { symbol, ...(options.params as Record<string, string> || {}) };
    if (bypassCache) {
      params.bypass_cache = 'true';
    }
    return this.get('/api/v1/indicators/matrix', { ...options, params });
  }

  /** 12. Indicator Status */
  public async getIndicatorsStatus(): Promise<{ status: string; active_indicators: number }> {
    return this.get('/api/v1/indicators/status');
  }

  /** 13. Market Daemon Status */
  public async getStatus(): Promise<{
    service?: string;
    is_running?: boolean;
    uptime_seconds?: number;
    total_ingested_count?: number;
    cached_candle_count?: number;
    audit_record_count?: number;
    error_count?: number;
    last_error?: string | null;
    last_cycle_utc?: string;
    telemetry_freshness?: {
      last_ingestion_utc?: string | null;
      last_derived_snapshot_utc?: string | null;
      last_price_probe_utc?: string | null;
      last_completed_cycle_utc?: string | null;
    };
    p95_latency_ms?: number;
    database?: {
      backend?: string;
      healthy?: boolean;
      pool_active?: number;
      pool_available?: number;
      replication_lag_seconds?: string;
      outbox_pending_count?: number;
      error?: string;
      [key: string]: unknown;
    };
    spot_symbols?: string[];
    perp_symbols?: string[];
    timeframes?: string[];
    active_providers?: Record<string, string>;
  }> {
    return this.get('/api/v1/market/status');
  }

  /** 14. Regime Backtest Observability (Stage 053B) */
  public async getRegimeBacktest(
    symbol: string = 'BTCUSDT',
    timeframe: string = '15m',
    horizonBars: number = 4
  ): Promise<RegimeBacktestResponse> {
    return this.get<RegimeBacktestResponse>('/api/v1/indicators/regime-backtest', {
      params: { symbol, timeframe, horizon_bars: horizonBars },
    });
  }

  /** 15. Multi-Asset Instruments Catalog (Stage 056) */
  public async getMarketInstruments(universe: string = 'crypto'): Promise<MarketInstrumentsResponse> {
    return this.get<MarketInstrumentsResponse>('/api/v1/market/instruments', {
      params: { universe },
    });
  }

  /** 16. Multi-Asset Comprehensive Market State (Stage 056) */
  public async getMarketState(symbols?: string): Promise<MarketStateResponse> {
    return this.get<MarketStateResponse>('/api/v1/market/state', {
      params: symbols ? { symbols } : undefined,
    });
  }

  /** 17. Multi-Timeframe Trend Matrix Projection (Stage 056) */
  public async getMarketTrendMatrix(universe: string = 'crypto'): Promise<TrendMatrixProjectionResponse> {
    return this.get<TrendMatrixProjectionResponse>('/api/v1/market/trend-matrix', {
      params: { universe },
    });
  }

  /** 18. MTC Multi-Timeframe Consensus Watchlist (Stage 056) */
  public async getMarketMtcWatchlist(symbols?: string): Promise<MtcWatchlistResponse> {
    return this.get<MtcWatchlistResponse>('/api/v1/market/mtc-watchlist', {
      params: symbols ? { symbols } : undefined,
    });
  }

  /** 19. Market Volatility Statistics (Stage 056) */
  public async getMarketVolatilityStats(symbols?: string): Promise<VolatilityStatisticsResponse> {
    return this.get<VolatilityStatisticsResponse>('/api/v1/market/volatility-statistics', {
      params: symbols ? { symbols } : undefined,
    });
  }

  /** 20. Data Continuity & Heartbeat Telemetry (Stage 056) */
  public async getMarketContinuity(symbols?: string): Promise<DataContinuityResponse> {
    return this.get<DataContinuityResponse>('/api/v1/market/continuity', {
      params: symbols ? { symbols } : undefined,
    });
  }

  /** 21. Crypto AI Executive Brief (Stage 060) */
  public async getCryptoBrief(options: RequestOptions = {}): Promise<CryptoAIBriefResponse> {
    return this.get<CryptoAIBriefResponse>('/api/v1/market/crypto/brief', options);
  }

  /** 22. 6H Crypto Chief Strategist Brief (Stage 071) */
  public async getStrategistBrief(options: RequestOptions = {}): Promise<StrategistReportContractV1> {
    return this.get<StrategistReportContractV1>('/api/v1/market/crypto/strategist/brief', options);
  }

  /** 23. 6H Crypto Desk Strategist Brief History (Stage 072) */
  public async getStrategistHistory(limit: number = 20, date?: string, options: RequestOptions = {}): Promise<Record<string, unknown>> {
    const params: Record<string, string | number> = { limit, ...(options.params || {}) };
    if (date) params.date = date;
    return this.get('/api/v1/desk/strategist/history', { ...options, params });
  }

  /** 24. Tag Attribution Sandbox (Stage 067/072) */
  public async getTagAttribution(params: Record<string, string | number>, options: RequestOptions = {}): Promise<Record<string, unknown>> {
    return this.get('/api/v1/lab/tag-attribution', { ...options, params: { ...params, ...(options.params || {}) } });
  }

  /** 25. Actionable Trade Advisory Blueprint (Stage 088 / Stage 090) */
  public async getTradeAdvisoryBlueprint(symbol?: string, timeframe?: string, options: RequestOptions = {}): Promise<TradeAdvisoryReportV1> {
    const params: Record<string, string | number> = { ...(options.params || {}) };
    if (symbol) params.symbol = symbol;
    if (timeframe) params.timeframe = timeframe;
    return this.get<TradeAdvisoryReportV1>('/api/v1/market/crypto/trade-advisory', { ...options, params });
  }
}

export interface RegimeBacktestMetricsResponse {
  completed_sample_count: number;
  win_rate_descriptive: string;
  gross_profit_factor: string | null;
  net_return_average_bps: string;
  coverage_ratio: string;
  data_quality: string;
}

export interface RegimeBacktestResponse {
  schema_version: string;
  symbol: string;
  timeframe: string;
  status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  reason_code: string;
  sample_count: number;
  required_samples: number;
  coverage_ratio: string;
  last_successful_update_utc: string | null;
  data_watermark_utc: string | null;
  metrics: RegimeBacktestMetricsResponse | null;
}

// Stage 056 Market Board Types
export interface MarketInstrumentItem {
  symbol: string;
  base_asset: string;
  quote_asset: string;
  category: string;
  status: string;
  price_precision: number;
  quantity_precision: number;
  tick_size: string;
  min_qty: string;
  contract_type: string;
}

export interface MarketInstrumentsResponse {
  universe: string;
  total: number;
  updated_at_utc: string;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  items: MarketInstrumentItem[];
}

export interface MarketStateDimensionScores {
  participation?: string | null;
  directional_intent?: string | null;
  conviction?: string | null;
  risk_appetite?: string | null;
  acceptance?: string | null;
  volatility_expansion?: string | null;
  trend_persistence?: string | null;
  exhaustion?: string | null;
}

export interface MarketStateRecordItem {
  symbol: string;
  last_price?: string | null;
  change_24h_pct?: string | null;
  high_24h?: string | null;
  low_24h?: string | null;
  volume_24h?: string | null;
  quote_volume_24h?: string | null;
  spread_bps?: string | null;
  market_state: string;
  dimensions?: MarketStateDimensionScores | null;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  bar_close_utc?: string | null;
  snapshot_as_of_utc: string;
  is_stale: boolean;
}

export interface MarketStateResponse {
  updated_at_utc: string;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  records: MarketStateRecordItem[];
}

export interface TrendMatrixCellItem {
  trend_direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  trend_label: string;
  score?: string | null;
  regime?: string | null;
  ema_alignment: string;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  bar_close_utc?: string | null;
}

export interface TrendMatrixProjectionResponse {
  universe: string;
  calculated_at_utc: string;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  symbols: string[];
  timeframes: string[];
  matrix: Record<string, Record<string, TrendMatrixCellItem>>;
}

export interface MtcWatchlistItemData {
  symbol: string;
  consensus_direction: 'LONG' | 'SHORT' | 'NEUTRAL';
  consensus_score: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | 'INSUFFICIENT_DATA';
  agreement_ratio: string;
  aligned_timeframes_count: number;
  total_timeframes_count: number;
  conflict_level: 'LOW' | 'MODERATE' | 'HIGH';
  regime_summary: string;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  bar_close_utc: string;
}

export interface MtcWatchlistResponse {
  updated_at_utc: string;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  items: MtcWatchlistItemData[];
}

export interface VolatilityItemData {
  symbol: string;
  timeframe: string;
  atr_14: string;
  atr_pct: string;
  realized_volatility_30d_pct: string;
  vol_percentile?: string | null;
  volatility_regime: string;
  sample_bars_count: number;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  bar_close_utc: string;
}

export interface VolatilityStatisticsResponse {
  updated_at_utc: string;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  items: VolatilityItemData[];
}

export interface DataContinuityItemData {
  symbol: string;
  feed_status: 'HEALTHY' | 'DEGRADED' | 'INTERRUPTED';
  last_bar_close_utc: string;
  ingest_lag_ms: number;
  clock_drift_ms: number;
  missing_bars_count_24h: number;
  integrity_ratio_24h: string;
  heartbeat_fsm_state: 'ONLINE' | 'DEGRADED' | 'OFFLINE';
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
}

export interface DataContinuityResponse {
  updated_at_utc: string;
  data_quality_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  items: DataContinuityItemData[];
}

// Stage 060 Crypto AI Brief Types
export interface ObservedFibonacciLevels {
  fib_support_382: string | null;
  fib_support_618: string | null;
  fib_resistance_382: string | null;
  fib_resistance_618: string | null;
}

// Stage 106 Tactical Space Types
export interface VolumeProfileSummary {
  poc: string;
  val: string;
  vah: string;
  lookback_bars: number;
  bin_count: number;
  target_value_area_pct: string;
  realized_value_area_pct: string;
}

export interface TacticalSpaceItem {
  timeframe: '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d';
  data_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  support_price?: string | null;
  resistance_price?: string | null;
  risk_reward_ratio?: string | null;
  is_falsified?: boolean | null;
  invalidation_condition?: string | null;
  volume_profile?: VolumeProfileSummary | null;
  unavailable_reason?: string | null;
  evaluation_candle_close_utc?: string | null;
}

export interface Core7BriefItem {
  symbol: string;
  spot_price: string | null;
  change_24h_pct: string | null;
  basis_bps: string | null;
  volume_profile_regime: string | null;
  observed_fibonacci_levels: ObservedFibonacciLevels;
  data_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  tactical_spaces?: Record<string, TacticalSpaceItem>;
}

export interface DirectionalLayers {
  sentiment_layer: 'BULLISH' | 'BEARISH' | 'NEUTRAL' | 'UNAVAILABLE';
  weather_layer: 'BULLISH' | 'BEARISH' | 'NEUTRAL' | 'UNAVAILABLE';
  indicator_layer: 'BULLISH' | 'BEARISH' | 'NEUTRAL' | 'UNAVAILABLE';
}

export interface CrossLayerAlignmentBrief {
  alignment_verdict: string;
  directional_layers: DirectionalLayers;
  skew_status: string;
  diagnostic_summary: string;
}

export interface MacroClimateBrief {
  primary_regime: string;
  sentiment_score: string | null;
  volatility_regime: string | null;
  regime_description: string;
}

export interface CryptoAIBriefResponse {
  schema_version: string;
  generated_at_utc: string;
  telemetry_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  snapshot_skew_seconds: string;
  macro_climate: MacroClimateBrief;
  cross_layer_alignment: CrossLayerAlignmentBrief;
  core7_briefs: Core7BriefItem[];
  trader_actionable_observations: string[];
}

export interface StrategistSymbolItemV1 {
  symbol: string;
  status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  trend_direction: 'LONG' | 'SHORT' | 'NEUTRAL';
  tactical_action: 'LONG_ACCUMULATE' | 'SHORT_DISTRIBUTE' | 'STAND_ASIDE';
  tactical_setup_eligible: boolean;
  reason: string | null;
  spot_price: string | null;
  perp_price: string | null;
  basis_bps: string | null;
  quote_age_seconds: number | null;
  quote_timestamp_utc?: string | null;
  entry_high: string | null;
  entry_low: string | null;
  invalidation_stop: string | null;
  target_take_profit: string | null;
  worst_band_risk: string | null;
  worst_band_reward: string | null;
  worst_band_rr: string | null;
  atr_14: string | null;
  swing_high_20: string | null;
  swing_low_20: string | null;
  ma_20: string | null;
  ma_50: string | null;
  open_interest: string | null;
  open_interest_reason: string;
  funding_rate: string | null;
  funding_rate_reason: string;
  liquidation_cluster: string | null;
  liquidation_cluster_reason: string;
  sentinel_status: 'ACTIVE_MONITORING' | 'INVALIDATED' | 'UNMONITORED_OFFLINE';
  sentinel_reason: string | null;
  sentinel_overlay?: StrategistSentinelOverlayV1 | null;
}

export interface SentinelSourceObservationV1 {
  source_id: string;
  source_provider: string;
  market_type: 'SPOT' | 'PERP';
  timeframe: 'TICKER_PROBE' | '15M' | '1H' | '4H' | '6H';
  source_timestamp_utc?: string | null;
  ingested_at_utc?: string | null;
  as_of: string;
  availability: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  null_reason?: string | null;
}

export interface StrategistSentinelOverlayV1 {
  schema_version: string;
  as_of: string;
  macro_anchor_id: string;
  macro_anchor_generation: number;
  generation_token: string;
  source_lineage: string;
  source_observations: SentinelSourceObservationV1[];
  overlay_status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  macro_fields_immutable: string[];
  sentinel_state: 'ACTIVE_MONITORING' | 'BREAKOUT_TESTING' | 'DEFENSE_TRIGGERED' | 'VOLATILITY_SURGE' | 'BASIS_DISLOCATION' | 'EXPIRED';
  sentinel_event_code: 'NONE' | 'BREAKOUT_SWING_HIGH' | 'BREAKDOWN_SWING_LOW' | 'VOLUME_SPIKE_15M' | 'ATR_EXPANSION_15M' | 'BASIS_DISLOCATION';
  intra_cycle_bias: 'NEUTRAL' | 'LEAN_BULLISH' | 'LEAN_BEARISH' | 'EXTREME_DEFENSE';
  sentinel_headline?: string | null;
  distance_to_swing_high_pct?: string | null;
  distance_to_swing_low_pct?: string | null;
  micro_volume_ratio?: string | null;
  tactical_invalidation_rule: string;
}

export interface StrategistReportContractV1 {
  schema_version: string;
  report_id: string;
  window_id: string;
  window_start_utc: string;
  window_end_utc: string;
  tactics_expiry_utc: string;
  display_grace_expiry_utc: string;
  anchor_bar_close_utc: string;
  generated_at_utc: string;
  status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  tactics_lifecycle: 'ACTIVE' | 'EXPIRED';
  is_updating?: boolean;
  coverage_count: number;
  total_symbols: number;
  symbols: Record<string, StrategistSymbolItemV1>;
  primary_scenario?: string | null;
  contingent_scenario?: string | null;
  narrative?: string | null;
  narrative_source?: string;
  provenance: Record<string, any>;
}

export interface TradeAdvisorySymbolBlueprintV1 {
  symbol: string;
  status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  regime_verdict: string;
  phase_classification: 'INCEPTION' | 'CONTINUATION' | 'EXHAUSTION' | 'COMPRESSION' | 'NEUTRAL_RANGE' | 'UNKNOWN';
  phase_description: string;
  tactical_action: 'LONG_ACCUMULATE' | 'SHORT_DISTRIBUTE' | 'STAND_ASIDE';
  confidence_score?: number | null;
  confidence_tier?: 'EXEMPLARY' | 'FAVORABLE' | 'MARGINAL' | 'UNFAVORABLE' | null;
  suggested_capital_allocation_pct: string;
  capital_allocation_rationale: string;
  current_price?: string | null;
  entry_high?: string | null;
  entry_low?: string | null;
  invalidation_stop?: string | null;
  target_take_profit?: string | null;
  worst_band_rr?: string | null;
  trailing_breakeven_trigger?: string | null;
  scale_out_target?: string | null;
  time_decay_horizon_bars?: number | null;
  td_setup_count?: number | null;
  td_setup_direction?: 'BUY' | 'SELL' | null;
  td_rhythm_guidance?: string | null;
  volume_speed_warning?: string | null;
  backtest_combo_win_rate?: string | null;
  close_time_utc?: string | null;
  ingested_at_utc?: string | null;
  tactics_valid_until_utc?: string | null;
  relative_entry_distance_pct?: string | null;
  bar_age_seconds?: number | null;
  quote_timestamp_utc?: string | null;
  quote_age_seconds?: number | null;
  causal_lag_seconds?: number | null;
  freshness_age_seconds?: number | null;
  stale_after_seconds: 600;
  source_lineage?: string | null;
  unavailable_reason?: string | null;
  grounding_sentinel_status: 'ACTIVE_MONITORING' | 'INVALIDATED' | 'UNMONITORED_OFFLINE';
}

export interface CadenceStatusV1 {
  timeframe: '5m' | '15m' | '30m' | '1h' | '4h' | '1d';
  anchor_bar_close_utc?: string | null;
  valid_until_utc?: string | null;
  status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  is_expired?: boolean | null;
  symbol_coverage_count: number;
}

export interface TradeAdvisoryReportV1 {
  schema_version: 'trade_advisory.v1';
  report_id: string;
  window_id: string;
  anchor_bar_close_utc: string;
  generated_at_utc: string;
  status: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  coverage_count: number;
  total_symbols?: number;
  symbols: Record<string, TradeAdvisorySymbolBlueprintV1>;
  primary_focus_symbol?: string | null;
  selected_timeframe?: string;
  selected_symbol?: string | null;
  is_all_cadences_expired?: boolean;
  cadences_status?: Record<string, CadenceStatusV1>;
}

export interface CanonicalInstrumentKeyV1 {
  asset_class?: string;
  exchange?: string;
  market_type?: string;
  symbol: string;
  price_type?: string;
  quote_asset?: string;
}

export interface CanonicalCandleV1 {
  candle_id?: string;
  instrument: string | CanonicalInstrumentKeyV1;
  open_time_utc: string;
  close_time_utc: string;
  candle_state?: string;
  open: DecimalString;
  high: DecimalString;
  low: DecimalString;
  close: DecimalString;
  volume: DecimalString;
}

export interface CanonicalCandlesResponseV1 {
  symbol: string;
  market_type: string;
  timeframe: string;
  count: number;
  format: string;
  candles: CanonicalCandleV1[];
}

export const apiClient = new ReadOnlyApiClient();
